import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Draggable, Droppable } from 'react-beautiful-dnd';
import { useStatsigClient } from '@statsig/react-bindings';

import { QUESTION_GROUP_LOGIC, QUESTION_GROUP_LOGIC_BRANCHES } from '@api/instant_survey';
import { GATES } from '@constants';
import {
  ActionOverlayPopover,
  Card,
  CopyIcon,
  DragDropV2Icon,
  FlowArrowIcon,
  FolderIcon,
  Layout,
  OverlayTrigger,
  ShuffleIcon,
  toast,
  Tooltip,
  TrashIcon,
} from '@utilities';
import { MAX_NAME_LENGTH } from '@utilities/helpers/validations';

import ModalQuestionGroupLogic from './components/ModalQuestionGroupLogic';
import Question from '../Question';
import ModalDeleteQuestionGroup from './components/ModalDeleteQuestionGroup';
import { QUESTION_GROUP_NAME_UNIQUE_ERROR } from '../../../../../../utilities/constants';
import { useSurveyContext } from '../../../../../../utilities/context';
import {
  createQuestionKey,
  getGroupIdsWithIntraGroupLogic,
  getQuestionsCost,
  isQuestionGroupNameTaken,
} from '../../../../../../utilities/helpers';

import styles from './_index.module.scss';

export const QUESTION_GROUP_DROPPABLE_PREFIX = 'question-group';
export const QUESTION_GROUP_DROPPABLE_TYPE = 'QUESTION_GROUP';

const QuestionGroupBlock = ({
  availableParentGroupsByGroupId,
  canPreviewSurvey,
  firstErrorRef,
  group,
  isDetailedQuestionView,
  isPreviewDisabled,
  items,
  onDuplicateQuestion,
  onEditQuestion,
  onPreviewSurvey,
  onRemoveQuestion,
  previewDisabledTooltip,
  provided,
  questionLogic,
  questions,
  selectedQuestions,
  setQuestionLogic,
  showActions,
}) => {
  const {
    assignGroupToParent,
    assignQuestionToGroup,
    copyGroup,
    currentQuestionCount,
    deleteGroup,
    isCopyingGroup,
    isReadOnly,
    maxQuestions,
    questionDisplayIndexById,
    renameGroup,
    survey,
    toggleGroupRandomize,
  } = useSurveyContext();

  const { client } = useStatsigClient();
  const showQuestionGroupBlocks = client.checkGate(GATES.QUESTION_GROUP_BLOCKS);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDropdownActive, setIsDropdownActive] = useState(false);
  const [isGroupLogicModalOpen, setIsGroupLogicModalOpen] = useState(false);
  const [groupName, setGroupName] = useState(group.name);
  const [isUpdatingParent, setIsUpdatingParent] = useState(false);

  // Update the input if the group name changes elsewhere (e.g. a draft reload); otherwise
  // it would keep showing the old name.
  useEffect(() => {
    setGroupName(group.name);
  }, [group.name]);

  const questionGroups = Object.values(survey?.questionGroups || {});

  const droppableId = `${QUESTION_GROUP_DROPPABLE_PREFIX}-${group.id}`;
  const droppableType = `${QUESTION_GROUP_DROPPABLE_TYPE}-${group.id}`;
  const directGroupQuestions = questions.filter(({ questionGroup }) => questionGroup === group.id);
  const isDuplicateDisabled =
    directGroupQuestions.length === 0 ||
    currentQuestionCount + getQuestionsCost(directGroupQuestions) > maxQuestions;
  const isRandomized = group.randomizeOrder ?? false;
  const parentGroup =
    group.parentGroup != null ? survey.questionGroups?.[String(group.parentGroup)] : null;
  const availableParentGroups = availableParentGroupsByGroupId[String(group.id)] || [];
  const randomizeActionLabel = isRandomized
    ? 'Remove question randomization'
    : 'Randomize question order';

  const hasIntraGroupLogic = getGroupIdsWithIntraGroupLogic({
    questionLogic,
    questions: selectedQuestions,
  }).includes(String(group.id));

  const groupLogicBranches = questionLogic?.[QUESTION_GROUP_LOGIC]?.[group.id];
  const hasQuestionGroupLogic = QUESTION_GROUP_LOGIC_BRANCHES.some(
    (branch) => (groupLogicBranches?.[branch]?.logic?.length ?? 0) > 0
  );

  const handleNameBlur = () => {
    const trimmed = groupName.trim();
    if (!trimmed || trimmed === group.name) {
      // reset display if blank
      setGroupName(group.name);
      return;
    }

    if (
      isQuestionGroupNameTaken({
        excludeGroupId: group.id,
        name: trimmed,
        questionGroups: survey.questionGroups,
      })
    ) {
      toast.error(QUESTION_GROUP_NAME_UNIQUE_ERROR);
      setGroupName(group.name);
      return;
    }

    renameGroup(group.id, trimmed);
  };

  const handleRandomizeToggle = () => {
    toggleGroupRandomize(group.id);
  };

  const handleParentGroupChange = async (parentGroupId) => {
    setIsUpdatingParent(true);
    try {
      await assignGroupToParent(group.id, parentGroupId);
      setIsDropdownActive(false);
    } catch {
      toast.error('Unable to move the question group. Please try again.');
    } finally {
      setIsUpdatingParent(false);
    }
  };

  const handleRemoveFromGroup = (question) => {
    // Unassign from group. If this was the last member, assignQuestionToGroup will
    // auto-DELETE the group via the context helper.
    assignQuestionToGroup(question, null);
  };

  /**
   * handleRemoveQuestionFromSurvey
   * When a question is fully removed from the survey (not just ungrouped), the group it belonged to
   * may be left empty. Compare against this group's DIRECT members only — `questions` also contains
   * nested child-group questions, so a parent group emptied of its own questions would otherwise
   * look occupied and survive as an empty wrapper.
   */
  const handleRemoveQuestionFromSurvey = (question) => {
    const targetKey = createQuestionKey(question);
    const remainingDirectMembers = directGroupQuestions.filter(
      (q) => createQuestionKey(q) !== targetKey
    );
    if (remainingDirectMembers.length === 0) {
      // Last direct member — ungroup first so the context helper flattens the group away.
      assignQuestionToGroup(question, null);
    }
    onRemoveQuestion(question);
  };

  return (
    <div
      className={styles['question-group-block-draggable-container']}
      data-is-nested={group.parentGroup != null}
      ref={provided.innerRef}
      {...provided.draggableProps}
    >
      <Card
        as={Layout.Flex.Column}
        className={styles['question-group-block-card']}
        elevation={2}
        gap="small"
      >
        <div className={styles['question-group-block-header']}>
          <div className={styles['question-group-block-title-wrapper']}>
            <div
              className={styles['question-group-block-drag-handle']}
              {...provided.dragHandleProps}
            >
              <DragDropV2Icon />
            </div>
            {hasQuestionGroupLogic && (
              <OverlayTrigger.Tooltip
                overlay={<Tooltip>This question group has logic applied</Tooltip>}
                placement="top"
              >
                <div className={styles['question-group-block-logic-icon']}>
                  <FlowArrowIcon />
                </div>
              </OverlayTrigger.Tooltip>
            )}
            <div className={styles['question-group-block-name-column']}>
              <input
                aria-label="Question group name"
                className={styles['question-group-block-name-input']}
                disabled={isReadOnly || !showActions}
                maxLength={MAX_NAME_LENGTH}
                onBlur={handleNameBlur}
                onChange={(e) => setGroupName(e.target.value)}
                type="text"
                value={groupName}
              />
              {parentGroup && (
                <span className={styles['question-group-block-parent-indicator']}>
                  Nested in {parentGroup.name}
                </span>
              )}
              {isRandomized && (
                <span className={styles['question-group-block-randomized-indicator']}>
                  The questions in this group are randomized.
                </span>
              )}
            </div>
          </div>

          {showActions && (
            <div className={styles['question-group-block-actions']}>
              <ActionOverlayPopover
                hasArrow={false}
                isActive={isDropdownActive}
                onClick={() => setIsDropdownActive(!isDropdownActive)}
                onHide={() => setIsDropdownActive(false)}
              >
                <ul>
                  <li>
                    <OverlayTrigger.Tooltip
                      overlay={
                        hasIntraGroupLogic ? (
                          <Tooltip className={styles['question-group-block-randomize-tooltip']}>
                            Randomization cannot be used because there is logic applied to a
                            question within this group
                          </Tooltip>
                        ) : (
                          <></>
                        )
                      }
                      placement="bottom"
                    >
                      <button
                        aria-label={randomizeActionLabel}
                        data-testid="randomize-question-order-button"
                        disabled={hasIntraGroupLogic || isUpdatingParent}
                        onClick={() => {
                          handleRandomizeToggle();
                          setIsDropdownActive(false);
                        }}
                      >
                        <ShuffleIcon />
                        {randomizeActionLabel}
                      </button>
                    </OverlayTrigger.Tooltip>
                  </li>
                  {showQuestionGroupBlocks && (
                    <li>
                      <button
                        aria-label="Question Group Logic"
                        data-testid="question-group-logic-button"
                        onClick={() => {
                          setIsGroupLogicModalOpen(true);
                          setIsDropdownActive(false);
                        }}
                      >
                        <FlowArrowIcon />
                        Question Group Logic
                      </button>
                    </li>
                  )}
                  {availableParentGroups.map((parentGroup) => (
                    <li key={`move-group-to-${parentGroup.id}`}>
                      <button
                        aria-label={`Move group to ${parentGroup.name}`}
                        data-testid="move-group-to-parent-button"
                        disabled={isUpdatingParent}
                        onClick={() => handleParentGroupChange(parentGroup.id)}
                      >
                        <FolderIcon />
                        Move to: {parentGroup.name}
                      </button>
                    </li>
                  ))}
                  {group.parentGroup != null && (
                    <li>
                      <button
                        aria-label="Remove group from parent"
                        data-testid="remove-group-from-parent-button"
                        disabled={isUpdatingParent}
                        onClick={() => handleParentGroupChange(null)}
                      >
                        <FolderIcon />
                        Remove from Parent Group
                      </button>
                    </li>
                  )}
                  <li>
                    <button
                      aria-label="Copy group"
                      data-testid="copy-question-group-button"
                      disabled={isDuplicateDisabled || isCopyingGroup}
                      onClick={() => {
                        copyGroup(group.id);
                        setIsDropdownActive(false);
                      }}
                      type="button"
                    >
                      <CopyIcon />
                      Copy group
                    </button>
                  </li>
                  <li>
                    <button
                      aria-label="Delete group"
                      onClick={() => {
                        setIsDeleteModalOpen(true);
                        setIsDropdownActive(false);
                      }}
                      type="button"
                    >
                      <TrashIcon />
                      Delete group
                    </button>
                  </li>
                </ul>
              </ActionOverlayPopover>
            </div>
          )}
        </div>

        <div className={styles['question-group-block-questions']}>
          <Droppable
            droppableId={droppableId}
            isDropDisabled={isReadOnly || !showActions}
            type={droppableType}
          >
            {(droppableProvided) => (
              <div
                className={styles['question-group-block-droppable-zone']}
                ref={droppableProvided.innerRef}
                {...droppableProvided.droppableProps}
              >
                {items.map((item, index) => {
                  if (item.type === 'group') {
                    const childGroup = survey.questionGroups?.[String(item.groupId)] ?? {
                      id: item.groupId,
                      name: '',
                      randomizeOrder: false,
                    };

                    return (
                      <Draggable
                        draggableId={`question-group-block-${item.groupId}`}
                        index={index}
                        isDragDisabled={isReadOnly || !showActions}
                        key={`question-group-block-${item.groupId}`}
                      >
                        {(draggableProvided) => (
                          <QuestionGroupBlock
                            availableParentGroupsByGroupId={availableParentGroupsByGroupId}
                            canPreviewSurvey={canPreviewSurvey}
                            firstErrorRef={firstErrorRef}
                            group={childGroup}
                            isDetailedQuestionView={isDetailedQuestionView}
                            isPreviewDisabled={isPreviewDisabled}
                            items={item.items}
                            onDuplicateQuestion={onDuplicateQuestion}
                            onEditQuestion={onEditQuestion}
                            onPreviewSurvey={onPreviewSurvey}
                            onRemoveQuestion={onRemoveQuestion}
                            provided={draggableProvided}
                            questionLogic={questionLogic}
                            questions={item.questions}
                            selectedQuestions={selectedQuestions}
                            setQuestionLogic={setQuestionLogic}
                            showActions={showActions}
                          />
                        )}
                      </Draggable>
                    );
                  }

                  const { flatIndex, question } = item;
                  const questionId = question.id ?? index;
                  return (
                    <Draggable
                      draggableId={`question-group-question-${group.id}-${questionId}`}
                      index={index}
                      isDragDisabled={isReadOnly || !showActions}
                      key={questionId}
                    >
                      {(draggableProvided) => (
                        <Question
                          canPreviewSurvey={canPreviewSurvey}
                          firstErrorRef={firstErrorRef}
                          index={flatIndex}
                          isDetailedQuestionView={isDetailedQuestionView}
                          isNested
                          isPreviewDisabled={isPreviewDisabled}
                          number={questionDisplayIndexById[question.id] ?? String(flatIndex + 1)}
                          onDuplicateQuestion={onDuplicateQuestion}
                          onEditQuestion={onEditQuestion}
                          onPreviewSurvey={onPreviewSurvey}
                          onRemoveFromGroup={handleRemoveFromGroup}
                          onRemoveQuestion={handleRemoveQuestionFromSurvey}
                          previewDisabledTooltip={previewDisabledTooltip}
                          provided={draggableProvided}
                          question={question}
                          questionLogic={questionLogic}
                          selectedQuestions={selectedQuestions}
                          setQuestionLogic={setQuestionLogic}
                          showActions={showActions}
                        />
                      )}
                    </Draggable>
                  );
                })}
                {droppableProvided.placeholder}
              </div>
            )}
          </Droppable>
        </div>
      </Card>

      {isDeleteModalOpen && (
        <ModalDeleteQuestionGroup
          onClose={() => setIsDeleteModalOpen(false)}
          onConfirm={async () => {
            await deleteGroup(group.id);
          }}
        />
      )}

      {isGroupLogicModalOpen && (
        <ModalQuestionGroupLogic
          onClose={() => setIsGroupLogicModalOpen(false)}
          questionGroups={questionGroups}
          questionLogic={questionLogic}
          questions={selectedQuestions}
          setQuestionLogic={setQuestionLogic}
        />
      )}
    </div>
  );
};

QuestionGroupBlock.propTypes = {
  availableParentGroupsByGroupId: PropTypes.objectOf(PropTypes.array).isRequired,
  canPreviewSurvey: PropTypes.bool.isRequired,
  firstErrorRef: PropTypes.object.isRequired,
  group: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    parentGroup: PropTypes.number,
    randomizeOrder: PropTypes.bool,
  }).isRequired,
  isDetailedQuestionView: PropTypes.bool.isRequired,
  isPreviewDisabled: PropTypes.bool,
  items: PropTypes.array.isRequired,
  onDuplicateQuestion: PropTypes.func.isRequired,
  onEditQuestion: PropTypes.func.isRequired,
  onPreviewSurvey: PropTypes.func.isRequired,
  onRemoveQuestion: PropTypes.func.isRequired,
  previewDisabledTooltip: PropTypes.string,
  provided: PropTypes.object.isRequired,
  questionLogic: PropTypes.object,
  questions: PropTypes.array.isRequired,
  selectedQuestions: PropTypes.array.isRequired,
  setQuestionLogic: PropTypes.func.isRequired,
  showActions: PropTypes.bool,
};

export default QuestionGroupBlock;
